import React, { createContext, useContext, useState, useCallback } from "react";

const AuthContext = createContext(null);
const STORAGE_KEY = "khumbai.auth.officerId";

// The entry flow is always Landing -> Login -> Dashboard, with no shortcuts.
//
// Every fresh page load starts signed out, so the app opens on Landing every
// time: a new tab, a reload, the browser reopened, the link handed to someone
// else. An earlier version restored the session from sessionStorage on start,
// which meant a reload jumped straight past Landing and Login into the
// console — convenient mid-session, but it broke the one flow this app is
// meant to demonstrate.
//
// The session is still written to sessionStorage, so anything that needs to
// resolve the signed-in officer during the session can, and so the value is
// cleared properly on sign-out. It is simply never read back at startup.
// In-app navigation is unaffected: that is React state, and it does not
// reload the page.
//
// The cost, stated plainly: pressing F5 while signed in returns you to
// Landing and you sign in again. If that becomes annoying in daily use,
// restoring `loadSession` below is a one-line change.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const login = useCallback((officer) => {
    setUser(officer);
    try {
      sessionStorage.setItem(STORAGE_KEY, officer.id);
    } catch {
      /* storage unavailable — session stays in memory only */
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
